/**
 * Legacy Template Parser
 * Converts old HTML-based templates to new ContentBuilder element format
 */

import { createElement } from './elementHelpers';

/**
 * Parse HTML template content into ContentBuilder elements
 * @param {String} htmlContent - HTML string from old templates
 * @returns {Array} Array of elements
 */
export const parseHTMLToElements = (htmlContent) => {
  if (!htmlContent || htmlContent.trim() === '') {
    return [];
  }

  const elements = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  // Process each top-level element in the body
  const bodyChildren = doc.body.children;

  for (let i = 0; i < bodyChildren.length; i++) {
    const element = parseHTMLElement(bodyChildren[i]);
    if (element) {
      elements.push(element);
    }
  }

  return elements;
};

/**
 * Parse a single HTML element into ContentBuilder element
 */
const parseHTMLElement = (htmlEl) => {
  const tagName = htmlEl.tagName.toLowerCase();
  const classList = Array.from(htmlEl.classList);

  // Headings
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    return createElement('heading', {
      content: {
        text: htmlEl.textContent,
        level: tagName,
        alignment: getAlignment(htmlEl),
        color: htmlEl.style.color || 'default'
      }
    });
  }

  // Paragraphs
  if (tagName === 'p' && classList.includes('form-paragraph')) {
    return createElement('paragraph', {
      content: {
        text: htmlEl.textContent,
        alignment: getAlignment(htmlEl),
        fontSize: getFontSize(htmlEl)
      }
    });
  }

  // Rich text blocks
  if (tagName === 'div' && classList.includes('form-richtext')) {
    return createElement('richtext', {
      content: {
        html: htmlEl.innerHTML,
        alignment: getAlignment(htmlEl),
        fontSize: getFontSize(htmlEl)
      }
    });
  }

  // Divider
  if (tagName === 'hr') {
    return createElement('divider', {
      content: {
        style: htmlEl.style.borderStyle || 'solid',
        width: htmlEl.style.width || '100%',
        color: htmlEl.style.borderColor || '#cccccc',
        spacing: 'medium'
      }
    });
  }

  // Form fields
  if (tagName === 'div' && classList.includes('form-field')) {
    return parseFormField(htmlEl);
  }

  // Grid Container (Visual Grid)
  if (tagName === 'div' && (classList.includes('visual-grid') || classList.includes('grid-container'))) {
    return parseGridContainer(htmlEl);
  }

  // Grid Cell
  if (tagName === 'div' && classList.includes('grid-cell')) {
    return parseGridCell(htmlEl);
  }

  // Unknown/unsupported element
  console.warn(`Unsupported HTML element: ${tagName}`, htmlEl);
  return null;
};

/**
 * Parse form field container
 */
const parseFormField = (fieldDiv) => {
  const label = fieldDiv.querySelector('.form-label');
  const labelText = label ? label.textContent.replace(/\s*\*\s*$/, '') : '';
  const required = label ? label.textContent.includes('*') : false;

  // Text input
  const textInput = fieldDiv.querySelector('input.form-text, input.form-input');
  if (textInput) {
    return createElement('text-input', {
      content: {
        label: labelText,
        name: textInput.getAttribute('name') || '',
        placeholder: textInput.getAttribute('placeholder') || '',
        required
      }
    });
  }

  // Textarea
  const textarea = fieldDiv.querySelector('textarea.form-textarea');
  if (textarea) {
    return createElement('textarea', {
      content: {
        label: labelText,
        name: textarea.getAttribute('name') || '',
        placeholder: textarea.getAttribute('placeholder') || '',
        required,
        rows: parseInt(textarea.getAttribute('rows')) || 4
      }
    });
  }

  // Select dropdown
  const select = fieldDiv.querySelector('select.form-select');
  if (select) {
    const options = [];
    const optionElements = select.querySelectorAll('option');
    optionElements.forEach(opt => {
      if (opt.value && opt.value !== '') {
        options.push(opt.textContent);
      }
    });

    return createElement('select', {
      content: {
        label: labelText,
        name: select.getAttribute('name') || '',
        options,
        required,
        placeholder: optionElements[0]?.textContent || 'Choose an option...'
      }
    });
  }

  // Checkbox
  const checkbox = fieldDiv.querySelector('input.form-checkbox[type="checkbox"]');
  if (checkbox) {
    const checkboxText = fieldDiv.querySelector('.form-checkbox-text');
    return createElement('checkbox', {
      content: {
        label: labelText,
        name: checkbox.getAttribute('name') || '',
        text: checkboxText ? checkboxText.textContent : 'I agree',
        required
      }
    });
  }

  // Radio group
  const radioGroup = fieldDiv.querySelector('.form-radio-group');
  if (radioGroup) {
    const radioInputs = radioGroup.querySelectorAll('input.form-radio[type="radio"]');
    const options = [];
    radioInputs.forEach(radio => {
      const radioText = radio.parentElement.querySelector('.form-radio-text');
      if (radioText) {
        options.push(radioText.textContent);
      } else if (radio.value) {
        options.push(radio.value);
      }
    });

    return createElement('radio', {
      content: {
        label: labelText,
        name: radioInputs[0]?.getAttribute('name') || '',
        options,
        required
      }
    });
  }

  // Signature field
  const signaturePad = fieldDiv.querySelector('.form-signature-pad');
  if (signaturePad) {
    return createElement('signature', {
      content: {
        label: labelText,
        name: signaturePad.getAttribute('data-signature-field') || 'signature',
        required,
        instructionText: signaturePad.getAttribute('data-instruction-text') || '',
        legalNoticeText: signaturePad.getAttribute('data-legal-notice-text') || ''
      }
    });
  }

  return null;
};

/**
 * Parse grid container (visual grid)
 */
const parseGridContainer = (gridDiv) => {
  const style = gridDiv.style;
  const children = [];

  // Parse grid properties from inline style
  let rows = 2;
  let columns = 2;
  let gap = 'medium';

  // Extract columns from grid-template-columns: repeat(N, 1fr)
  if (style.gridTemplateColumns) {
    const colMatch = style.gridTemplateColumns.match(/repeat\((\d+),/);
    if (colMatch) {
      columns = parseInt(colMatch[1]);
    }
  }

  // Extract rows from grid-template-rows: repeat(N, 1fr)
  if (style.gridTemplateRows) {
    const rowMatch = style.gridTemplateRows.match(/repeat\((\d+),/);
    if (rowMatch) {
      rows = parseInt(rowMatch[1]);
    }
  }

  // Extract gap
  if (style.gap) {
    gap = style.gap;
    // Convert pixel values to size names
    if (gap.includes('0.5rem') || gap.includes('8px')) gap = 'small';
    else if (gap.includes('1rem') || gap.includes('16px')) gap = 'medium';
    else if (gap.includes('1.5rem') || gap.includes('24px')) gap = 'large';
  }

  // Parse child grid cells
  const cellChildren = gridDiv.children;
  for (let i = 0; i < cellChildren.length; i++) {
    const cellElement = parseHTMLElement(cellChildren[i]);
    if (cellElement) {
      children.push(cellElement);
    }
  }

  return createElement('grid-container', {
    props: {
      rows,
      columns,
      gap,
      minRowHeight: '100px',
      backgroundColor: style.backgroundColor || 'transparent'
    },
    children
  });
};

/**
 * Parse grid cell
 */
const parseGridCell = (cellDiv) => {
  const style = cellDiv.style;
  const children = [];

  // Parse cell properties
  const padding = cellDiv.getAttribute('data-padding') || 'medium';
  const backgroundColor = style.backgroundColor || 'transparent';

  // Parse children (look inside grid-cell-content if it exists)
  const contentWrapper = cellDiv.querySelector('.grid-cell-content');
  const childContainer = contentWrapper || cellDiv;

  const cellChildren = childContainer.children;
  for (let i = 0; i < cellChildren.length; i++) {
    const childElement = parseHTMLElement(cellChildren[i]);
    if (childElement) {
      children.push(childElement);
    }
  }

  return createElement('grid-cell', {
    props: {
      rowSpan: 1,
      columnSpan: 1,
      padding,
      verticalAlign: 'top',
      horizontalAlign: 'left',
      backgroundColor
    },
    children
  });
};

/**
 * Helper: Extract text alignment from inline style
 */
const getAlignment = (element) => {
  const textAlign = element.style.textAlign;
  if (textAlign && ['left', 'center', 'right', 'justify'].includes(textAlign)) {
    return textAlign;
  }
  return 'left';
};

/**
 * Helper: Extract font size category from inline style
 */
const getFontSize = (element) => {
  const fontSize = element.style.fontSize;
  if (!fontSize) return 'normal';

  if (fontSize.includes('0.875') || fontSize.includes('14px') || fontSize.includes('small')) {
    return 'small';
  }
  if (fontSize.includes('1.125') || fontSize.includes('18px') || fontSize.includes('large')) {
    return 'large';
  }
  return 'normal';
};

/**
 * Validate parsed elements
 */
export const validateParsedElements = (elements) => {
  if (!Array.isArray(elements)) {
    return { valid: false, error: 'Elements must be an array' };
  }

  if (elements.length === 0) {
    return { valid: false, error: 'No elements found in template' };
  }

  // Check each element has required fields
  for (const el of elements) {
    if (!el.id || !el.type) {
      return { valid: false, error: 'Element missing id or type', element: el };
    }

    if (!el.content && !el.children) {
      return { valid: false, error: 'Element missing content or children', element: el };
    }
  }

  return { valid: true };
};

/**
 * Extract legacy element structure from HTML (for backward compatibility testing)
 */
export const extractLegacyStructure = (htmlContent) => {
  const elements = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  const formFields = doc.querySelectorAll('.form-field');
  formFields.forEach((field, idx) => {
    const label = field.querySelector('.form-label');
    const input = field.querySelector('input, textarea, select');

    if (input) {
      elements.push({
        index: idx,
        type: input.tagName.toLowerCase(),
        name: input.getAttribute('name'),
        label: label ? label.textContent : '',
        required: input.hasAttribute('required')
      });
    }
  });

  return elements;
};
